import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config';
import { 
  DollarSign, 
  Search, 
  Clock, 
  CheckCircle, 
  XCircle,
  X,
  Eye,
  Settings,
  AlertTriangle,
  Info,
  ShoppingCart,
  CreditCard,
  Filter,
  RefreshCw,
  ArrowRight,
  FileText,
  TrendingUp,
  MessageSquare,
  Building2
} from 'lucide-react';

const RefundManagement = () => {
  const [refunds, setRefunds] = useState([]);
  const [loadingRefunds, setLoadingRefunds] = useState(false);
  const [refundStats, setRefundStats] = useState(null);
  const [refundFilters, setRefundFilters] = useState({
    status: '',
    reason: '',
    search: '',
    page: 1,
    limit: 20
  });
  const [pagination, setPagination] = useState({});
  const [selectedRefund, setSelectedRefund] = useState(null);
  const [showRefundDetails, setShowRefundDetails] = useState(false);
  const [processingAction, setProcessingAction] = useState(null);
  
  // Modal states
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [approveNotes, setApproveNotes] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [processForm, setProcessForm] = useState({
    refundMethod: '',
    refundReference: '',
    refundReceipt: '',
    notes: ''
  });

  useEffect(() => {
    fetchRefundStats();
  }, []);

  useEffect(() => {
    fetchRefunds();
  }, [refundFilters]);

  const fetchRefunds = async () => {
    setLoadingRefunds(true);
    try {
      const token = localStorage.getItem('adminToken');
      const queryParams = new URLSearchParams({
        page: refundFilters.page.toString(),
        limit: refundFilters.limit.toString()
      });

      if (refundFilters.status) queryParams.append('status', refundFilters.status);
      if (refundFilters.reason) queryParams.append('reason', refundFilters.reason);
      if (refundFilters.search) queryParams.append('search', refundFilters.search);

      const response = await fetch(`${API_BASE_URL}/api/admin/refunds?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setRefunds(data.refunds || []);
        setPagination(data.pagination || {});
      }
    } catch (error) {
      console.error('Error fetching refunds:', error);
    } finally {
      setLoadingRefunds(false);
    }
  };

  const fetchRefundStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/refunds/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const stats = await response.json();
        setRefundStats(stats);
      }
    } catch (error) {
      console.error('Error fetching refund stats:', error);
    }
  };

  const openApproveModal = (refundId) => {
    setSelectedRefund(refunds.find(r => r.id === refundId));
    setApproveNotes('');
    setShowApproveModal(true);
  };

  const handleApproveRefund = async () => {
    if (!selectedRefund) return;

    setProcessingAction(selectedRefund.id);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/refunds/${selectedRefund.id}/approve`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ notes: approveNotes || 'Approved by admin' })
      });

      if (response.ok) {
        setShowApproveModal(false);
        setSelectedRefund(null);
        setApproveNotes('');
        fetchRefunds();
        fetchRefundStats();
      } else {
        const error = await response.json();
        alert(`❌ Failed to approve: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error approving refund:', error);
      alert('❌ Error approving refund');
    } finally {
      setProcessingAction(null);
    }
  };

  const openProcessModal = (refundId) => {
    const refund = refunds.find(r => r.id === refundId);
    setSelectedRefund(refund);
    // Pre-fill with original payment method as default
    const defaultMethod = refund?.payment?.method || '';
    setProcessForm({
      refundMethod: defaultMethod,
      refundReference: '',
      refundReceipt: '',
      notes: ''
    });
    setShowProcessModal(true);
  };

  const handleProcessRefund = async () => {
    if (!selectedRefund) return;
    if (!processForm.refundMethod.trim()) {
      alert('Please select a refund method');
      return;
    }
    if (!processForm.refundReference.trim()) {
      alert('Please enter a refund reference/transaction number');
      return;
    }

    setProcessingAction(selectedRefund.id);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/refunds/${selectedRefund.id}/process`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          refundMethod: processForm.refundMethod,
          refundReference: processForm.refundReference,
          refundReceipt: processForm.refundReceipt || processForm.refundReference,
          notes: processForm.notes
        })
      });

      if (response.ok) {
        setShowProcessModal(false);
        setSelectedRefund(null);
        setProcessForm({ refundMethod: '', refundReference: '', refundReceipt: '', notes: '' });
        fetchRefunds();
        fetchRefundStats();
      } else {
        const error = await response.json();
        alert(`❌ Failed to process: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error processing refund:', error);
      alert('❌ Error processing refund');
    } finally {
      setProcessingAction(null);
    }
  };

  const openRejectModal = (refundId) => {
    setSelectedRefund(refunds.find(r => r.id === refundId));
    setRejectReason('');
    setShowRejectModal(true);
  };

  const handleRejectRefund = async () => {
    if (!selectedRefund || !rejectReason.trim()) {
      alert('Please provide a rejection reason');
      return;
    }

    setProcessingAction(selectedRefund.id);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/refunds/${selectedRefund.id}/reject`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ rejectionReason: rejectReason })
      });

      if (response.ok) {
        setShowRejectModal(false);
        setSelectedRefund(null);
        setRejectReason('');
        fetchRefunds();
        fetchRefundStats();
      } else {
        const error = await response.json();
        alert(`❌ Failed to reject: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error rejecting refund:', error);
      alert('❌ Error rejecting refund');
    } finally {
      setProcessingAction(null);
    }
  };

  const getRefundStatusColor = (status) => {
    switch (status) {
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      case 'APPROVED':
        return 'bg-blue-100 text-blue-800';
      case 'PROCESSING':
        return 'bg-purple-100 text-purple-800';
      case 'COMPLETED':
        return 'bg-green-100 text-green-800';
      case 'REJECTED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getRefundStatusIcon = (status) => {
    switch (status) {
      case 'PENDING':
        return <Clock className="h-4 w-4 text-yellow-600" />;
      case 'APPROVED':
        return <CheckCircle className="h-4 w-4 text-blue-600" />;
      case 'PROCESSING':
        return <Settings className="h-4 w-4 text-purple-600" />;
      case 'COMPLETED':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'REJECTED':
        return <XCircle className="h-4 w-4 text-red-600" />;
      default:
        return <Clock className="h-4 w-4 text-gray-600" />;
    }
  };

  const formatCurrency = (amount) => {
    return `KES ${(amount || 0).toLocaleString()}`;
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString();
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Refund Management</h2>
        <p className="text-gray-600 text-sm">
          Manage refunds for cancelled orders. Refunds are automatically created when orders are cancelled.
        </p>
      </div>

      {/* Important Notice */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 p-4 mb-6 rounded-r-lg shadow-sm">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <Info className="h-6 w-6 text-blue-600 mr-3 mt-0.5" />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-semibold text-blue-900 mb-2 flex items-center">
              <AlertTriangle className="h-5 w-5 mr-2" />
              Refund System - Important Information
            </h3>
            <div className="space-y-2 text-sm text-blue-800">
              <p>
                <strong>✅ Automatic Refund Creation:</strong> When you cancel a <strong>confirmed order</strong>, a refund request is automatically created in the system. No manual action needed!
              </p>
              <p>
                <strong>📋 Eligibility Rules:</strong>
              </p>
              <ul className="list-disc list-inside ml-2 space-y-1">
                <li>Only orders that were <strong>CONFIRMED</strong> and then <strong>CANCELLED by admin</strong> are eligible</li>
                <li>Only orders with <strong>COMPLETED payments</strong> can be refunded</li>
                <li>Orders with <strong>failed payments</strong> are NOT eligible for refunds</li>
                <li>Refunds follow a two-step approval process: <strong>Admin Approval</strong> → <strong>Finance Processing</strong></li>
              </ul>
              <p className="mt-2 text-xs text-blue-700 bg-blue-100 p-2 rounded">
                <strong>💡 Tip:</strong> To refund an order, simply cancel it from the Orders page if it's in CONFIRMED status. The refund will be created automatically!
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Refund Statistics - Interactive Cards */}
      {refundStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div 
            onClick={() => setRefundFilters({...refundFilters, status: '', page: 1})}
            className="bg-white rounded-lg shadow-md p-6 cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-105"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Refunds</p>
                <p className="text-2xl font-bold text-blue-600">{refundStats.total || 0}</p>
                <p className="text-xs text-gray-500 mt-1">Click to view all</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div 
            onClick={() => setRefundFilters({...refundFilters, status: 'PENDING', page: 1})}
            className={`bg-white rounded-lg shadow-md p-6 cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-105 ${
              refundFilters.status === 'PENDING' ? 'ring-2 ring-yellow-500' : ''
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pending Approval</p>
                <p className="text-2xl font-bold text-yellow-600">{refundStats.pending || 0}</p>
                <p className="text-xs text-yellow-600 mt-1 font-medium">Click to view ({refundStats.pending || 0} requests)</p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
          </div>

          <div 
            onClick={() => setRefundFilters({...refundFilters, status: 'COMPLETED', page: 1})}
            className={`bg-white rounded-lg shadow-md p-6 cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-105 ${
              refundFilters.status === 'COMPLETED' ? 'ring-2 ring-green-500' : ''
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Completed</p>
                <p className="text-2xl font-bold text-green-600">{refundStats.completed || 0}</p>
                <p className="text-xs text-green-600 mt-1 font-medium">Click to view ({refundStats.completed || 0} refunds)</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Amount</p>
                <p className="text-2xl font-bold text-red-600">
                  {formatCurrency(refundStats.totalAmount || 0)}
                </p>
                <p className="text-xs text-gray-500 mt-1">All refunded amounts</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-64">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by refund number, order number, or customer..."
                value={refundFilters.search}
                onChange={(e) => setRefundFilters({...refundFilters, search: e.target.value, page: 1})}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100] focus:border-transparent"
              />
            </div>
          </div>
          
          <select
            value={refundFilters.status}
            onChange={(e) => setRefundFilters({...refundFilters, status: e.target.value, page: 1})}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100] focus:border-transparent"
          >
            <option value="">All Statuses</option>
            <option value="PENDING">Pending Approval</option>
            <option value="APPROVED">Approved</option>
            <option value="PROCESSING">Processing</option>
            <option value="COMPLETED">Completed</option>
            <option value="REJECTED">Rejected</option>
          </select>

          <select
            value={refundFilters.reason}
            onChange={(e) => setRefundFilters({...refundFilters, reason: e.target.value, page: 1})}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100] focus:border-transparent"
          >
            <option value="">All Reasons</option>
            <option value="ADMIN_CANCELLATION">Admin Cancellation</option>
            <option value="CUSTOMER_REQUEST">Customer Request</option>
            <option value="PRODUCT_DEFECT">Product Defect</option>
            <option value="WRONG_ITEM">Wrong Item</option>
            <option value="DAMAGED_IN_SHIPPING">Damaged in Shipping</option>
            <option value="OTHER">Other</option>
          </select>

          <button
            onClick={() => {
              setRefundFilters({
                status: '',
                reason: '',
                search: '',
                page: 1,
                limit: 20
              });
              fetchRefunds();
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Reset
          </button>
        </div>
      </div>

      {/* Refunds Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Refund #
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Order
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Reason
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loadingRefunds ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#d44100]"></div>
                    </div>
                  </td>
                </tr>
              ) : refunds.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                    <DollarSign className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                    <p className="font-medium">No refunds found</p>
                    <p className="text-sm mt-1">
                      {refundFilters.search || refundFilters.status || refundFilters.reason
                        ? 'Try adjusting your filters'
                        : 'Refunds will appear here when orders are cancelled'}
                    </p>
                  </td>
                </tr>
              ) : (
                refunds.map((refund) => (
                  <tr key={refund.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{refund.refundNumber}</div>
                      {refund.order?.orderNumber && (
                        <div className="text-xs text-gray-500">Order: {refund.order.orderNumber}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {refund.order?.orderNumber || 'N/A'}
                      </div>
                      {refund.order?.status && (
                        <div className="text-xs text-gray-500">Status: {refund.order.status}</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {refund.customer?.fullName || refund.customer?.name || 'N/A'}
                      </div>
                      {refund.customer?.email && (
                        <div className="text-xs text-gray-500">{refund.customer.email}</div>
                      )}
                      {refund.customer?.phone && (
                        <div className="text-xs text-gray-500">{refund.customer.phone}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-gray-900">
                        {formatCurrency(refund.amount)}
                      </div>
                      {refund.payment?.method && (
                        <div className="text-xs text-gray-500">{refund.payment.method}</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {refund.reason === 'ADMIN_CANCELLATION' ? 'Admin Cancellation' : 
                         refund.reason.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}
                      </div>
                      {refund.customReason && (
                        <div className="text-xs text-gray-500 mt-1">{refund.customReason}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getRefundStatusIcon(refund.status)}
                        <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRefundStatusColor(refund.status)}`}>
                          {refund.status}
                        </span>
                      </div>
                      {refund.adminApproved && (
                        <div className="text-xs text-gray-500 mt-1">Admin approved</div>
                      )}
                      {refund.financeApproved && (
                        <div className="text-xs text-gray-500 mt-1">Finance approved</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div>{formatDate(refund.createdAt)}</div>
                      {refund.processedAt && (
                        <div className="text-xs mt-1">Processed: {formatDate(refund.processedAt)}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => {
                            setSelectedRefund(refund);
                            setShowRefundDetails(true);
                          }}
                          className="text-blue-600 hover:text-blue-900"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        {refund.status === 'PENDING' && (
                          <>
                            <button
                              onClick={() => openApproveModal(refund.id)}
                              disabled={processingAction === refund.id}
                              className="text-green-600 hover:text-green-900 disabled:opacity-50"
                              title="Approve Refund"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => openRejectModal(refund.id)}
                              disabled={processingAction === refund.id}
                              className="text-red-600 hover:text-red-900 disabled:opacity-50"
                              title="Reject Refund"
                            >
                              <XCircle className="h-4 w-4" />
                            </button>
                          </>
                        )}
                        {refund.status === 'APPROVED' && (
                          <button
                            onClick={() => openProcessModal(refund.id)}
                            disabled={processingAction === refund.id}
                            className="text-purple-600 hover:text-purple-900 disabled:opacity-50"
                            title="Process Refund"
                          >
                            {processingAction === refund.id ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                            ) : (
                              <Settings className="h-4 w-4" />
                            )}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination && pagination.pages > 1 && (
          <div className="bg-gray-50 px-6 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="text-sm text-gray-700">
              Showing page {pagination.page} of {pagination.pages} ({pagination.total} total)
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setRefundFilters({...refundFilters, page: refundFilters.page - 1})}
                disabled={refundFilters.page <= 1}
                className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setRefundFilters({...refundFilters, page: refundFilters.page + 1})}
                disabled={refundFilters.page >= pagination.pages}
                className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Refund Details Modal */}
      {showRefundDetails && selectedRefund && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-800">Refund Details</h3>
                <button
                  onClick={() => {
                    setShowRefundDetails(false);
                    setSelectedRefund(null);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Refund Overview */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-800 mb-3">Refund Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Refund Number</p>
                      <p className="font-medium">{selectedRefund.refundNumber}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Status</p>
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getRefundStatusColor(selectedRefund.status)}`}>
                        {getRefundStatusIcon(selectedRefund.status)}
                        <span className="ml-2">{selectedRefund.status}</span>
                      </span>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Amount</p>
                      <p className="font-semibold text-lg">{formatCurrency(selectedRefund.amount)}</p>
                    </div>
                      <div>
                        <p className="text-sm text-gray-600">Reason</p>
                        <p className="font-medium">
                          {selectedRefund.reason === 'ADMIN_CANCELLATION' 
                            ? 'Admin Cancellation' 
                            : selectedRefund.reason.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}
                        </p>
                        {selectedRefund.customReason && (
                          <p className="text-xs text-gray-500 mt-1">{selectedRefund.customReason}</p>
                        )}
                      </div>
                  </div>
                </div>

                {/* Order Information */}
                {selectedRefund.order && (
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                      <ShoppingCart className="h-5 w-5 mr-2" />
                      Order Information
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Order Number</p>
                        <p className="font-medium">{selectedRefund.order.orderNumber}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Order Status</p>
                        <p className="font-medium">{selectedRefund.order.status}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Order Total</p>
                        <p className="font-medium">{formatCurrency(selectedRefund.order.totalAmount || selectedRefund.order.total)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Order Date</p>
                        <p className="font-medium">{formatDate(selectedRefund.order.createdAt)}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Payment Information */}
                {selectedRefund.payment && (
                  <div className="bg-green-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                      <CreditCard className="h-5 w-5 mr-2" />
                      Payment Information
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Payment Method</p>
                        <p className="font-medium">{selectedRefund.payment.method}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Payment Status</p>
                        <p className="font-medium">{selectedRefund.payment.status}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Payment Amount</p>
                        <p className="font-medium">{formatCurrency(selectedRefund.payment.amount)}</p>
                      </div>
                      {selectedRefund.payment.transactionId && (
                        <div>
                          <p className="text-sm text-gray-600">Transaction ID</p>
                          <p className="font-medium text-xs">{selectedRefund.payment.transactionId}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Customer Information */}
                {selectedRefund.customer && (
                  <div className="bg-purple-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-800 mb-3">Customer Information</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Name</p>
                        <p className="font-medium">{selectedRefund.customer.fullName || selectedRefund.customer.name}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Email</p>
                        <p className="font-medium">{selectedRefund.customer.email || 'N/A'}</p>
                      </div>
                      {selectedRefund.customer.phone && (
                        <div>
                          <p className="text-sm text-gray-600">Phone</p>
                          <p className="font-medium">{selectedRefund.customer.phone}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Timeline */}
                <div className="bg-yellow-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-800 mb-3">Refund Timeline</h4>
                  <div className="space-y-2">
                    <div className="flex items-center text-sm">
                      <Clock className="h-4 w-4 text-gray-500 mr-2" />
                      <span className="text-gray-600">Requested:</span>
                      <span className="ml-2 font-medium">{formatDate(selectedRefund.createdAt)}</span>
                    </div>
                    {selectedRefund.adminApprovedAt && (
                      <div className="flex items-center text-sm">
                        <CheckCircle className="h-4 w-4 text-blue-500 mr-2" />
                        <span className="text-gray-600">Admin Approved:</span>
                        <span className="ml-2 font-medium">{formatDate(selectedRefund.adminApprovedAt)}</span>
                      </div>
                    )}
                    {selectedRefund.financeApprovedAt && (
                      <div className="flex items-center text-sm">
                        <CheckCircle className="h-4 w-4 text-purple-500 mr-2" />
                        <span className="text-gray-600">Finance Approved:</span>
                        <span className="ml-2 font-medium">{formatDate(selectedRefund.financeApprovedAt)}</span>
                      </div>
                    )}
                    {selectedRefund.processedAt && (
                      <div className="flex items-center text-sm">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                        <span className="text-gray-600">Processed:</span>
                        <span className="ml-2 font-medium">{formatDate(selectedRefund.processedAt)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Description */}
                {selectedRefund.description && (
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Description</h4>
                    <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">{selectedRefund.description}</p>
                  </div>
                )}

                {/* Refund Method */}
                {selectedRefund.refundMethod && (
                  <div className="bg-indigo-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-800 mb-2">Refund Processing Details</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Refund Method</p>
                        <p className="font-medium">{selectedRefund.refundMethod}</p>
                      </div>
                      {selectedRefund.refundReference && (
                        <div>
                          <p className="text-sm text-gray-600">Reference Number</p>
                          <p className="font-medium text-xs">{selectedRefund.refundReference}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    onClick={() => {
                      setShowRefundDetails(false);
                      setSelectedRefund(null);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Close
                  </button>
                  {selectedRefund.status === 'PENDING' && (
                    <>
                      <button
                        onClick={() => {
                          setShowRefundDetails(false);
                          openApproveModal(selectedRefund.id);
                        }}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => {
                          setShowRefundDetails(false);
                          openRejectModal(selectedRefund.id);
                        }}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                      >
                        Reject
                      </button>
                    </>
                  )}
                  {selectedRefund.status === 'APPROVED' && (
                    <button
                      onClick={() => {
                        setShowRefundDetails(false);
                        openProcessModal(selectedRefund.id);
                      }}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                    >
                      Process Refund
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Approve Refund Modal */}
      {showApproveModal && selectedRefund && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-800 flex items-center">
                  <CheckCircle className="h-6 w-6 text-green-600 mr-2" />
                  Approve Refund Request
                </h3>
                <button
                  onClick={() => {
                    setShowApproveModal(false);
                    setSelectedRefund(null);
                    setApproveNotes('');
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="mb-4">
                <div className="bg-blue-50 border-l-4 border-blue-500 p-3 mb-4">
                  <p className="text-sm text-blue-800">
                    <strong>Refund #{selectedRefund.refundNumber}</strong> will be approved and moved to the <strong>Finance Department</strong> for processing.
                  </p>
                </div>
                
                <div className="space-y-2 mb-4">
                  <p className="text-sm text-gray-600">
                    <strong>Order:</strong> {selectedRefund.order?.orderNumber || 'N/A'}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Amount:</strong> {formatCurrency(selectedRefund.amount)}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Payment Method:</strong> {selectedRefund.payment?.method || 'N/A'}
                  </p>
                </div>

                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Admin Notes (Optional)
                </label>
                <textarea
                  value={approveNotes}
                  onChange={(e) => setApproveNotes(e.target.value)}
                  placeholder="Add any notes for the finance team..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  rows={4}
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowApproveModal(false);
                    setSelectedRefund(null);
                    setApproveNotes('');
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApproveRefund}
                  disabled={processingAction === selectedRefund.id}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {processingAction === selectedRefund.id ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Approving...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Approve Refund
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject Refund Modal */}
      {showRejectModal && selectedRefund && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-800 flex items-center">
                  <XCircle className="h-6 w-6 text-red-600 mr-2" />
                  Reject Refund Request
                </h3>
                <button
                  onClick={() => {
                    setShowRejectModal(false);
                    setSelectedRefund(null);
                    setRejectReason('');
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="mb-4">
                <div className="bg-red-50 border-l-4 border-red-500 p-3 mb-4">
                  <p className="text-sm text-red-800">
                    <AlertTriangle className="h-5 w-5 inline mr-2" />
                    This action cannot be undone. The refund request will be permanently rejected.
                  </p>
                </div>
                
                <div className="space-y-2 mb-4">
                  <p className="text-sm text-gray-600">
                    <strong>Refund #:</strong> {selectedRefund.refundNumber}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Order:</strong> {selectedRefund.order?.orderNumber || 'N/A'}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Amount:</strong> {formatCurrency(selectedRefund.amount)}
                  </p>
                </div>

                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rejection Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Please provide a detailed reason for rejecting this refund request..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  rows={4}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  This reason will be recorded and visible in the refund history.
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowRejectModal(false);
                    setSelectedRefund(null);
                    setRejectReason('');
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRejectRefund}
                  disabled={processingAction === selectedRefund.id || !rejectReason.trim()}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {processingAction === selectedRefund.id ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Rejecting...
                    </>
                  ) : (
                    <>
                      <XCircle className="h-4 w-4 mr-2" />
                      Reject Refund
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Process Refund Modal */}
      {showProcessModal && selectedRefund && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-800 flex items-center">
                  <Building2 className="h-6 w-6 text-purple-600 mr-2" />
                  Process Refund Payment
                </h3>
                <button
                  onClick={() => {
                    setShowProcessModal(false);
                    setSelectedRefund(null);
                    setProcessForm({ refundMethod: '', refundReference: '', refundReceipt: '', notes: '' });
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="mb-4">
                <div className="bg-purple-50 border-l-4 border-purple-500 p-3 mb-4">
                  <p className="text-sm text-purple-800">
                    <Info className="h-5 w-5 inline mr-2" />
                    <strong>Finance Processing:</strong> Process the refund payment based on the original payment method used.
                  </p>
                </div>

                {/* Refund Summary */}
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <h4 className="font-semibold text-gray-800 mb-3">Refund Summary</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-gray-600">Refund #</p>
                      <p className="font-medium">{selectedRefund.refundNumber}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Order #</p>
                      <p className="font-medium">{selectedRefund.order?.orderNumber || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Amount</p>
                      <p className="font-semibold text-lg">{formatCurrency(selectedRefund.amount)}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Original Payment Method</p>
                      <p className="font-medium">{selectedRefund.payment?.method || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Payment Method Selection */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Refund Method <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={processForm.refundMethod}
                    onChange={(e) => setProcessForm({...processForm, refundMethod: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select refund method...</option>
                    <option value="MPESA">M-Pesa</option>
                    <option value="BANK_TRANSFER">Bank Transfer</option>
                    <option value="PAYPAL">PayPal</option>
                    <option value="CARD">Card Refund</option>
                    <option value="CASH_ON_DELIVERY">Cash on Delivery</option>
                    <option value="MOBILE_MONEY">Mobile Money</option>
                    <option value="DIGITAL_WALLET">Digital Wallet</option>
                    {selectedRefund.payment?.method && (
                      <option value={selectedRefund.payment.method}>Original Method ({selectedRefund.payment.method})</option>
                    )}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    💡 Recommended: Use the same payment method as the original order for faster processing.
                  </p>
                </div>

                {/* Refund Reference */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Refund Reference/Transaction Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={processForm.refundReference}
                    onChange={(e) => setProcessForm({...processForm, refundReference: e.target.value})}
                    placeholder="e.g., M-Pesa transaction code, bank reference, PayPal transaction ID..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Enter the transaction reference number from the refund payment provider.
                  </p>
                </div>

                {/* Refund Receipt */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Receipt Number (Optional)
                  </label>
                  <input
                    type="text"
                    value={processForm.refundReceipt}
                    onChange={(e) => setProcessForm({...processForm, refundReceipt: e.target.value})}
                    placeholder="e.g., Receipt number if available..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                {/* Processing Notes */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Processing Notes (Optional)
                  </label>
                  <textarea
                    value={processForm.notes}
                    onChange={(e) => setProcessForm({...processForm, notes: e.target.value})}
                    placeholder="Add any additional notes about the refund processing..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    rows={3}
                  />
                </div>

                {/* Workflow Info */}
                <div className="bg-blue-50 rounded-lg p-3 mb-4">
                  <p className="text-xs text-blue-800">
                    <strong>Next Steps:</strong> After processing, the refund status will be updated to <strong>COMPLETED</strong> and the customer will be notified.
                  </p>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowProcessModal(false);
                    setSelectedRefund(null);
                    setProcessForm({ refundMethod: '', refundReference: '', refundReceipt: '', notes: '' });
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleProcessRefund}
                  disabled={processingAction === selectedRefund.id || !processForm.refundMethod.trim() || !processForm.refundReference.trim()}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {processingAction === selectedRefund.id ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <Settings className="h-4 w-4 mr-2" />
                      Complete Refund Processing
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RefundManagement;

